<script setup lang="ts">
import { computed } from 'vue'

import { useCall } from '../../composables/useCall'
import { useCallStore } from '../../stores/call'

const callStore = useCallStore()
const { endCall } = useCall()

const show = computed(() => callStore.outgoingCallPending)

const characterName = computed(() => {
  return callStore.callCharacterName || '未知'
})

const characterAvatar = computed(() => {
  return callStore.callCharacterAvatar || '/default-avatar.png'
})

async function handleCancel() {
  await endCall()
}
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div
        v-if="show"
        fixed inset-0 z-300
        class="outgoing-call-backdrop"
        flex="~ col" items-center justify-between
        p-8 text-white
      >
        <!-- 呼叫信息 - 居中 -->
        <div flex-1 flex="~ col" items-center justify-center>
          <!-- 头像 - 带呼吸动画 -->
          <div relative>
            <img
              :src="characterAvatar"
              :alt="characterName"
              class="outgoing-avatar"
              h-36 w-36 rounded-full object-cover
              border="3 white/40"
            >
            <!-- 多重呼吸光环效果 -->
            <div
              absolute inset-0
              class="outgoing-ring ring-1"
              rounded-full
              border="3 blue-400"
            />
            <div
              absolute inset-0
              class="outgoing-ring ring-2"
              rounded-full
              border="3 blue-300"
            />
            <div
              absolute inset-0
              class="outgoing-ring ring-3"
              rounded-full
              border="3 blue-200"
            />
          </div>

          <!-- 角色名称 -->
          <p mt-8 text-4xl tracking-wide>
            {{ characterName }}
          </p>

          <!-- 状态指示 -->
          <div mt-6 flex="~ col" items-center gap-3>
            <!-- 加载动画点 -->
            <div flex gap-2>
              <div class="calling-pulse-dot delay-0" />
              <div class="calling-pulse-dot delay-1" />
              <div class="calling-pulse-dot delay-2" />
            </div>
            <p text-xl>
              正在呼叫...
            </p>
          </div>

          <!-- 提示文字 -->
          <p mt-4 text-sm text-white tracking-wide op-60>
            等待对方接听
          </p>
        </div>

        <!-- 挂断按钮 - 底部 -->
        <div w-full flex justify-center pb-8>
          <div text-center>
            <button
              class="cancel-button"
              h-22 w-22 flex items-center justify-center rounded-full
              transition="all duration-300"
              @click="handleCancel"
            >
              <div i-carbon:phone-off h-9 w-9 />
            </button>
            <p mt-4 text-sm tracking-wide>
              取消
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* 外拨背景 */
.outgoing-call-backdrop {
 background: linear-gradient(135deg,
 #172554 0%,
 #1e3a8a 50%,
 #172554 100%
 );
 animation: outgoingGradient 10s ease infinite;
 background-size: 200% 200%;
}

@keyframes outgoingGradient {
 0%, 100% {
 background-position: 0% 50%;
 }
 50% {
 background-position: 100% 50%;
 }
}

/* 外拨头像 */
.outgoing-avatar {
 box-shadow: 0 16px 48px rgba(59, 130, 246, 0.5),
 0 0 0 4px rgba(255, 255, 255, 0.2);
 animation: outgoingAvatarFloat 3s ease-in-out infinite;
}

@keyframes outgoingAvatarFloat {
 0%, 100% {
 transform: translateY(0) rotate(0deg);
 }
 50% {
 transform: translateY(-15px) rotate(2deg);
 }
}

/* 外拨光环 */
.outgoing-ring {
 pointer-events: none;
 opacity: 0;
}

.ring-1 {
 animation: outgoingRingPulse 2s ease-out infinite;
 animation-delay: 0s;
}

.ring-2 {
 animation: outgoingRingPulse 2s ease-out infinite;
 animation-delay: 0.4s;
}

.ring-3 {
 animation: outgoingRingPulse 2s ease-out infinite;
 animation-delay: 0.8s;
}

@keyframes outgoingRingPulse {
 0% {
 transform: scale(1);
 opacity: 1;
 }
 100% {
 transform: scale(2.8);
 opacity: 0;
 }
}

/* 呼叫脉冲点 */
.calling-pulse-dot {
 width: 12px;
 height: 12px;
 background: linear-gradient(135deg, #60a5fa, #3b82f6);
 border-radius: 50%;
 box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
 animation: pulseDotBounce 1.5s ease-in-out infinite;
}

.delay-0 {
 animation-delay: 0s;
}

.delay-1 {
 animation-delay: 0.3s;
}

.delay-2 {
 animation-delay: 0.6s;
}

@keyframes pulseDotBounce {
 0%, 60%, 100% {
 transform: scale(1);
 opacity: 1;
 }
 30% {
 transform: scale(1.6);
 opacity: 0.6;
 }
}

/* 取消按钮 */
.cancel-button {
 background: linear-gradient(135deg, #ef4444, #dc2626);
 border: 2px solid rgba(255, 255, 255, 0.3);
 box-shadow: 0 10px 30px rgba(239, 68, 68, 0.5);
 animation: cancelButtonBreathe 3s ease-in-out infinite;
}

.cancel-button:hover {
 background: linear-gradient(135deg, #dc2626, #b91c1c);
 transform: scale(1.15);
 box-shadow: 0 14px 40px rgba(239, 68, 68, 0.7);
 animation: none;
}

.cancel-button:active {
 transform: scale(0.95);
}

@keyframes cancelButtonBreathe {
 0%, 100% {
 box-shadow: 0 10px 30px rgba(239, 68, 68, 0.5);
 }
 50% {
 box-shadow: 0 10px 40px rgba(239, 68, 68, 0.7);
 }
}

/* 过渡动画 */
.dialog-enter-active,
.dialog-leave-active {
 transition: opacity 0.4s ease;
}

/* 离开过渡期间立即禁用指针事件，防止遮挡下层 CallModal 的交互 */
.dialog-leave-active {
 pointer-events: none !important;
}

.dialog-enter-from,
.dialog-leave-to {
 opacity: 0;
}

.dialog-enter-active > div,
.dialog-leave-active > div {
 transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.dialog-enter-from > div:first-child {
 transform: scale(0.6);
 opacity: 0;
}

.dialog-leave-to > div:first-child {
 transform: scale(0.6);
 opacity: 0;
}
</style>
